import { Mic, MicOff, PhoneCall, PhoneOff, Volume2 } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ApiError } from "@/lib/api";
import { signedInThisPageLoad } from "@/lib/auth";
import { cn } from "@/lib/utils";
import {
  FALL_MS, decayLevel, deviceLabel, listAudioDevices, meterLevel, micPermission, probe, readAudioPrefs,
  shouldOfferSetup, speakerChoiceSupported, SPEAKING, subscribeAudioPrefs, testToneWav,
  writeAudioPrefs, type AudioDevice, type AudioPrefs, type MicPermission,
} from "@/softphone/audio-devices";
import { useSoftphone } from "@/softphone/softphone-context";
import { NO_RING_MS, soundTestPhase } from "@/softphone/sound-test";

/**
 * Setting the phone up before the first call, rather than during it.
 *
 * The browser asks for the microphone the first time a page reaches for one, and until this
 * existed that moment was `verto.answer`: the rep's first call rang, they pressed Answer, and
 * Chrome dropped a permission bar over the page while the lead heard nothing. This opens right
 * after sign-in instead — the one moment in a shift when nobody is waiting on the line — asks
 * for the microphone there, and lets the rep pick which microphone and which speaker while they
 * can still hear themselves think.
 *
 * Three rules it keeps:
 *
 * - **It opens itself at every sign-in, and only at a sign-in.** Whatever this browser settled on
 *   before: a rep moves desks, shares a machine, or unplugs the headset they picked last month,
 *   and the first anyone hears of it is a lead who cannot hear them; confirming the meter and the
 *   two pickers costs a keypress. But a page *reload* is not a sign-in, and a dialog over every
 *   refresh is nagging rather than helpful — so the offer is gated on
 *   `signedInThisPageLoad()`, which only a credential exchange in this page load sets. The other
 *   two ways in are unchanged and deliberate: the phone's options menu and the account page.
 * - **It never opens over a call.** A dialog that steals focus from a ringing call is the exact
 *   failure it exists to prevent, so it waits for the phone to be idle.
 * - **What is picked applies at once.** There is no Save: the speaker re-points the live call's
 *   audio through `subscribeAudioPrefs`, and the microphone is what the next call captures from
 *   (a live call's microphone cannot change without renegotiating the media, which the copy says
 *   rather than pretending otherwise).
 *
 * The same dialog is the one the rep reopens later from the phone's options menu or their
 * settings page, which is why it is a provider over the app and not something the dock owns.
 */

/* ------------------------------------------------------------------------------------------ */
/* Opening it, from anywhere                                                                   */
/* ------------------------------------------------------------------------------------------ */

/**
 * The dialog is opened by a plain function call, not through a context.
 *
 * Three surfaces reach for it — the phone's options menu, the settings page, and the dialog's own
 * offer at sign-in — and two of them are components that are also rendered on their own in tests
 * and in the `/dev/phone` gallery, over a mock phone and with no provider above them. A required
 * provider would make those mounts throw for the sake of one menu item, so what travels is a
 * module-level subscription, exactly as the saved devices do.
 */
type OpenListener = () => void;
const openListeners = new Set<OpenListener>();

export function openAudioSetup(): void {
  for (const listener of [...openListeners]) listener();
}

/** What the browser will do about the microphone, shared so every reader agrees and one asks. */
let permissionState: MicPermission | null = null;
const permissionListeners = new Set<(state: MicPermission) => void>();

export async function refreshMicPermission(): Promise<MicPermission> {
  const state = await micPermission();
  permissionState = state;
  for (const listener of [...permissionListeners]) listener(state);
  return state;
}

function setMicPermission(state: MicPermission): void {
  permissionState = state;
  for (const listener of [...permissionListeners]) listener(state);
}

/**
 * The permission, for a component that wants to say something about it.
 *
 * Reads it once for the whole app and re-reads nothing on its own: `permissions.query` does not
 * ask the rep anything, but it is also not a subscription, so the value is refreshed at the
 * points where it can have changed — the dialog taking the microphone, and the rep coming back
 * after changing a site setting.
 */
export function useMicPermission(): MicPermission | null {
  const [state, setState] = React.useState<MicPermission | null>(permissionState);
  React.useEffect(() => {
    permissionListeners.add(setState);
    if (permissionState === null) void refreshMicPermission();
    else setState(permissionState);
    return () => {
      permissionListeners.delete(setState);
    };
  }, []);
  return state;
}

/** The saved devices, for a summary that has to stay current while the dialog is changing them. */
export function useAudioPrefs(): AudioPrefs {
  const [prefs, setPrefs] = React.useState<AudioPrefs>(readAudioPrefs);
  React.useEffect(() => {
    setPrefs(readAudioPrefs());
    return subscribeAudioPrefs(setPrefs);
  }, []);
  return prefs;
}

/**
 * The chosen devices by name, for a summary that is not the dialog.
 *
 * Names come from the browser and only once the microphone has been granted — an unasked page
 * sees a list of blank labels — so before that this reports the saved choice as the system
 * default, which is also what a call would use. A device that is saved but not plugged in says
 * so, since "Jabra headset" beside a call that came out of the laptop speakers is worse than no
 * summary at all.
 */
export function useAudioDeviceNames(): { mic: string; speaker: string } {
  const prefs = useAudioPrefs();
  const permission = useMicPermission();
  const [devices, setDevices] = React.useState<{ mics: AudioDevice[]; speakers: AudioDevice[] }>(
    { mics: [], speakers: [] },
  );
  React.useEffect(() => {
    let cancelled = false;
    void listAudioDevices().then((found) => {
      if (!cancelled) setDevices(found);
    });
    return () => {
      cancelled = true;
    };
    // Re-read when the rep changes a device or grants the microphone: the second of those is
    // what turns a list of blanks into a list of names.
  }, [prefs, permission]);
  return {
    mic: deviceLabel(devices.mics, prefs.micId),
    speaker: speakerChoiceSupported()
      ? deviceLabel(devices.speakers, prefs.speakerId)
      : "Whatever this computer is set to",
  };
}

/**
 * Mounted once, over the routes: it holds the dialog and decides when to offer it.
 *
 * Inside the phone's provider, because a user with no endpoint has no microphone question to
 * answer — an admin who never takes a call should not be asked for one.
 */
export function AudioSetupHost() {
  const phone = useSoftphone();
  const [open, setOpen] = React.useState(false);
  const permission = useMicPermission();
  // One offer per sign-in. Without it the dialog would come back at every reconnect and every
  // navigation, which is nagging rather than helpful.
  const offered = React.useRef(false);

  React.useEffect(() => {
    const listener = () => setOpen(true);
    openListeners.add(listener);
    return () => {
      openListeners.delete(listener);
    };
  }, []);

  React.useEffect(() => {
    if (offered.current || permission === null || !phone.available) return;
    // Never over a call: a dialog that takes focus off a ringing call is the exact failure this
    // feature exists to prevent.
    if (phone.calls.length > 0) return;
    // Reloading a signed-in console is not signing in. Without this the dialog came up on every
    // refresh, which is the one thing this feature must not become.
    if (!signedInThisPageLoad()) return;
    offered.current = true;
    if (shouldOfferSetup(permission)) setOpen(true);
  }, [permission, phone.available, phone.calls.length]);

  // Signing out takes the phone away; whoever signs in next on this browser gets their own offer.
  React.useEffect(() => {
    if (!phone.available) offered.current = false;
  }, [phone.available]);

  return (
    <AudioSetupDialog
      open={open}
      onOpenChange={setOpen}
      permission={permission}
      onPermission={setMicPermission}
    />
  );
}

/* ------------------------------------------------------------------------------------------ */
/* The dialog                                                                                  */
/* ------------------------------------------------------------------------------------------ */

function AudioSetupDialog({
  open, onOpenChange, permission, onPermission,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  permission: MicPermission | null;
  onPermission: (state: MicPermission) => void;
}) {
  const [devices, setDevices] = React.useState<{ mics: AudioDevice[]; speakers: AudioDevice[] }>(
    { mics: [], speakers: [] },
  );
  const [prefs, setPrefs] = React.useState<AudioPrefs>(readAudioPrefs);
  const [stream, setStream] = React.useState<MediaStream | null>(null);
  const [asking, setAsking] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const speakerChoice = speakerChoiceSupported();

  // The microphone is held only while this dialog is on screen — the browser lights an indicator
  // for as long as a page has one, and a console that kept it open all day would have every rep
  // looking at a recording light between calls.
  const release = React.useCallback(() => {
    setStream((current) => {
      current?.getTracks().forEach((track) => track.stop());
      return null;
    });
  }, []);

  React.useEffect(() => () => release(), [release]);

  /** Take the microphone, which is what makes the browser ask, then list what it has. */
  const take = React.useCallback(
    async (micId: string | null) => {
      setAsking(true);
      setProblem(null);
      try {
        const opened = await probe(micId);
        release();
        setStream(opened);
        onPermission("granted");
        setDevices(await listAudioDevices());
      } catch (e) {
        const name = e instanceof DOMException ? e.name : "";
        if (name === "NotAllowedError" || name === "SecurityError") {
          onPermission("denied");
          setProblem(null);
        } else if (name === "NotFoundError" || name === "OverconstrainedError") {
          // The saved device is gone, or there is no microphone at all. Falling straight back to
          // the default is better than an error the rep cannot act on — a call would have done
          // the same, and this way the level meter shows them what it picked.
          if (micId) {
            setPrefs((p) => ({ ...p, micId: null }));
            void take(null);
            return;
          }
          setProblem("This computer has no microphone the browser can use.");
        } else {
          setProblem("The browser could not open the microphone. Try again.");
          console.error("[audio-setup] getUserMedia failed", e);
        }
      } finally {
        setAsking(false);
      }
    },
    [onPermission, release],
  );

  // Opening with the microphone already granted goes straight to the meter: the rep came here to
  // change a device, and a picker with no level on it cannot show them they picked the right one.
  React.useEffect(() => {
    if (!open) {
      release();
      return;
    }
    const saved = readAudioPrefs();
    setPrefs(saved);
    setProblem(null);
    if (permission === "granted") void take(saved.micId);
    else void listAudioDevices().then(setDevices);
    // `permission` is deliberately not a dependency: it changes to "granted" inside `take`, and
    // reacting to that would take the microphone a second time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const choose = (change: Partial<AudioPrefs>) => {
    const next = { ...prefs, ...change };
    setPrefs(next);
    writeAudioPrefs(next);
    if (change.micId !== undefined) void take(next.micId);
  };

  const close = () => onOpenChange(false);

  /**
   * Done plays the chime and closes, in that order. The click is a deliberate gesture, which is
   * what a browser wants before a page may make a sound on its own, so the first ring after this
   * is never the first sound the page tries to play. It is also the last check the dialog can
   * give: a rep who hears nothing as it closes knows, before any call, that the speaker they
   * picked is not the one on their head. Best effort — a speaker the browser refuses is reported
   * by the Test button; here the dialog closes either way.
   */
  const done = () => {
    void playTestTone(prefs.speakerId).catch((e: unknown) => {
      console.error("[audio-setup] chime on Done failed", e);
    });
    close();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent size="md" aria-describedby="audio-setup-description">
        <DialogHeader>
          <DialogTitle>Set up your microphone and speaker</DialogTitle>
          <DialogDescription id="audio-setup-description">
            Your phone uses this browser. Getting this out of the way now means a call never
            arrives with a permission bar over it.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {permission === null ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : permission === "unavailable" ? (
            <Callout tone="danger">
              This browser will not give a page the microphone over an insecure connection. Open
              the console at its https address and set the phone up there.
            </Callout>
          ) : permission === "denied" ? (
            <DeniedHelp onRecheck={() => void micPermission().then(onPermission)} busy={asking} />
          ) : permission === "prompt" ? (
            <div className="space-y-3">
              <p className="text-sm text-text-muted">
                The browser will ask whether this site may use your microphone. Choose{" "}
                <strong className="font-medium text-text">Allow</strong> — without it your phone
                can ring, but nobody on the call will hear you.
              </p>
              {problem && <Callout tone="danger">{problem}</Callout>}
            </div>
          ) : (
            <div className="space-y-4">
              <Field
                label="Microphone"
                htmlFor="audio-setup-mic"
                hint="What the person on the call hears. A change applies to your next call."
              >
                <DevicePicker
                  id="audio-setup-mic"
                  devices={devices.mics}
                  value={prefs.micId}
                  onChange={(micId) => choose({ micId })}
                  defaultLabel="System default microphone"
                />
              </Field>

              <MicLevel stream={stream} busy={asking} />

              <Field
                label="Speaker"
                htmlFor="audio-setup-speaker"
                hint={
                  speakerChoice
                    ? "Where you hear the call and the ringer. A change applies straight away, even on a call."
                    : "This browser always plays through whatever the computer is set to. Change it there."
                }
                action={
                  <SpeakerTest
                    speakerId={prefs.speakerId}
                    disabled={!speakerChoice}
                    onProblem={setProblem}
                  />
                }
              >
                <DevicePicker
                  id="audio-setup-speaker"
                  devices={devices.speakers}
                  value={prefs.speakerId}
                  onChange={(speakerId) => choose({ speakerId })}
                  defaultLabel="System default speaker"
                  disabled={!speakerChoice}
                />
              </Field>

              {problem && <Callout tone="danger">{problem}</Callout>}

              <SoundTestCall />
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          {permission === "granted" ? (
            <>
              <p className="self-center text-xs text-text-muted sm:mr-auto">
                You will hear a short chime as this closes. Silence means the speaker above is
                not the one you are listening on.
              </p>
              <Button variant="default" onClick={done}>
                Done
              </Button>
            </>
          ) : permission === "denied" || permission === "unavailable" ? (
            // Nothing here can undo either of these — the browser has to be changed, and the body
            // says how — so the dialog offers no button that would fail if it were pressed.
            <Button variant="default" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Not now
              </Button>
              <Button variant="default" loading={asking} onClick={() => void take(prefs.micId)}>
                <Mic />
                Allow microphone
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A real call, to hear yourself through the switch (ADR-062 in `platform`).
 *
 * The meter above proves the browser hears the microphone and the chime proves the speaker
 * plays. Neither proves the part that fails on a real floor: audio leaving the browser, crossing
 * the network to the switch, and the switch's audio coming back. This does — the switch rings
 * this phone, plays a prompt and a tone, records the rep and plays them back — so hearing their
 * own voice means a caller will hear them too.
 *
 * The phone answers the call itself (`startSoundTest`), and the test ends with the call: closing
 * the dialog hangs it up, because a test nobody is listening to is only a line held open.
 *
 * Exported for the `/dev/phone` gallery, which renders it over a pretend phone.
 */
export function SoundTestCall() {
  const phone = useSoftphone();
  const [callId, setCallId] = React.useState<string | null>(null);
  const [seen, setSeen] = React.useState(false);
  const [starting, setStarting] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);

  const call = callId ? phone.calls.find((c) => c.id === callId) : undefined;
  React.useEffect(() => {
    if (call && !seen) setSeen(true);
  }, [call, seen]);
  const phase = soundTestPhase(callId, call, seen);

  // A switch that cannot reach this phone tells the bus, not the browser: silence is the answer.
  React.useEffect(() => {
    if (phase !== "placing") return;
    const timer = setTimeout(() => {
      setCallId(null);
      setProblem("Your phone did not ring. Check that it is connected, then try again.");
    }, NO_RING_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  // Closing the dialog ends the test. Refs, so the cleanup sees the call as it is at the end and
  // not as it was when the dialog opened.
  const liveRef = React.useRef<string | null>(null);
  liveRef.current = call ? (callId ?? null) : null;
  const hangupRef = React.useRef(phone.hangup);
  hangupRef.current = phone.hangup;
  React.useEffect(
    () => () => {
      if (liveRef.current) void hangupRef.current(liveRef.current).catch(() => undefined);
    },
    [],
  );

  const otherCall = phone.calls.some((c) => c.id !== callId);
  const connected = phone.registration === "registered";
  const blocked = !connected
    ? "Your phone is not connected, so it cannot ring."
    : otherCall
      ? "Finish the call you are on first."
      : null;

  const start = async () => {
    setProblem(null);
    setSeen(false);
    setStarting(true);
    try {
      setCallId(await phone.startSoundTest());
    } catch (e) {
      setCallId(null);
      setProblem(e instanceof ApiError ? e.message : "Could not start the test call. Try again.");
      console.error("[audio-setup] sound test failed to start", e);
    } finally {
      setStarting(false);
    }
  };

  const busy = starting || phase === "placing" || phase === "ringing";
  const says =
    phase === "placing"
      ? "Calling your phone…"
      : phase === "ringing"
        ? "Your phone is ringing. It answers by itself."
        : phase === "connected"
          ? "Listen for the tone, then say a few words. When you stop talking, you hear them played back."
          : phase === "ended"
            ? "Test finished. Did you hear yourself?"
            : "Your phone rings and answers by itself. After the tone, say a few words: they are " +
              "played back to you through the phone system, the way a caller would hear you.";

  return (
    <section aria-labelledby="sound-test-title" className="space-y-3 border-t border-border pt-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 id="sound-test-title" className="text-sm font-medium text-text">
            Test call
          </h3>
          <p className="text-xs text-text-muted" aria-live="polite">
            {says}
          </p>
        </div>
        {phase === "connected" && callId ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => void phone.hangup(callId).catch(() => undefined)}
          >
            <PhoneOff />
            Hang up
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            loading={busy}
            disabled={!!blocked || busy}
            onClick={() => void start()}
          >
            <PhoneCall />
            {phase === "ended" ? "Test again" : "Start test call"}
          </Button>
        )}
      </div>

      {phase === "ended" && (
        <ul className="list-disc space-y-1 pl-5 text-xs text-text-muted">
          <li>
            <span className="font-medium text-text">You heard yourself:</span> your microphone and
            speaker work on calls.
          </li>
          <li>
            <span className="font-medium text-text">You heard the prompt but not yourself:</span>{" "}
            your microphone is not reaching the call. Pick another microphone above and test again.
          </li>
          <li>
            <span className="font-medium text-text">You heard nothing:</span> pick another speaker
            above and test again.
          </li>
        </ul>
      )}

      {blocked && phase !== "connected" && !busy && (
        <p className="text-xs text-text-muted">{blocked}</p>
      )}
      {problem && <Callout tone="danger">{problem}</Callout>}
    </section>
  );
}

/**
 * What to do about a microphone the browser is refusing.
 *
 * A page cannot re-ask once the rep (or their IT department) has said no — `getUserMedia` fails
 * immediately, with no prompt — so the only honest thing here is where the setting lives and a
 * way to look again once they have changed it.
 */
function DeniedHelp({ onRecheck, busy }: { onRecheck: () => void; busy: boolean }) {
  return (
    <div className="space-y-3">
      <Callout tone="warning">
        This browser is blocking the microphone for the console, so a call would connect with
        nobody able to hear you.
      </Callout>
      <ol className="list-decimal space-y-1 pl-5 text-sm text-text-muted">
        <li>Click the icon at the left of the address bar (a lock, a slider, or a camera).</li>
        <li>Set Microphone to Allow.</li>
        <li>Reload this page, then come back here.</li>
      </ol>
      <Button variant="outline" size="sm" loading={busy} onClick={onRecheck}>
        Check again
      </Button>
    </div>
  );
}

function DevicePicker({
  id, devices, value, onChange, defaultLabel, disabled,
}: {
  id: string;
  devices: readonly AudioDevice[];
  value: string | null;
  onChange: (id: string | null) => void;
  defaultLabel: string;
  disabled?: boolean;
}) {
  // Radix's Select has no value for "nothing chosen", and an empty string is reserved by it, so
  // the system default is a real option with a sentinel id rather than a cleared field.
  const DEFAULT = "__default__";
  const known = value && devices.some((d) => d.id === value) ? value : DEFAULT;
  return (
    <Select
      value={known}
      onValueChange={(next) => onChange(next === DEFAULT ? null : next)}
      disabled={disabled || devices.length === 0}
    >
      <SelectTrigger id={id}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={DEFAULT}>{defaultLabel}</SelectItem>
        {devices.map((device) => (
          <SelectItem key={device.id} value={device.id}>
            {device.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * The rep's own voice, as a bar.
 *
 * The only part of this dialog that proves anything: a picked device with no signal on it is a
 * muted headset or the wrong input, and finding that out here is the whole point of setting up
 * before a call rather than during one.
 */
function MicLevel({ stream, busy }: { stream: MediaStream | null; busy: boolean }) {
  const level = useMicLevel(stream);
  const heard = level > SPEAKING;
  return (
    <div className="space-y-1.5" aria-hidden={false}>
      <div className="flex items-center gap-2">
        {heard ? (
          <Mic className="size-4 shrink-0 text-success-11" />
        ) : (
          <MicOff className="size-4 shrink-0 text-text-subtle" />
        )}
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-sunken">
          <div
            className={cn(
              "h-full rounded-full transition-[width] duration-75",
              heard ? "bg-success-9" : "bg-gray-7",
            )}
            style={{ width: `${Math.round(level * 100)}%` }}
          />
        </div>
      </div>
      <p className="text-xs text-text-muted">
        {busy
          ? "Opening the microphone…"
          : !stream
            ? "No microphone open."
            : heard
              ? "The bar moves when you speak. That is what the caller will hear."
              : "Say something — the bar should move. If it does not, pick another microphone."}
      </p>
    </div>
  );
}

/**
 * A stream's loudness as a fraction of the bar, sampled every frame.
 *
 * Web Audio rather than anything on the track itself: `MediaStreamTrack` reports whether it is
 * live, never how loud it is. The context is closed with the stream, because a page that leaves
 * audio contexts open collects them (browsers cap how many a document may have).
 *
 * The three parts that make it look like a meter rather than a jittering line all live in
 * `audio-devices.ts`, where they are tested: RMS over the window (a peak reading strobes on every
 * consonant), `meterLevel` for the decibel scale that puts speech in the middle of the bar, and
 * `decayLevel` for the fall. Only a visible change is pushed into React state — at 60 frames a
 * second a `setState` per frame re-renders the dialog for movements nobody can see.
 */
function useMicLevel(stream: MediaStream | null): number {
  const [level, setLevel] = React.useState(0);
  React.useEffect(() => {
    if (!stream) {
      setLevel(0);
      return;
    }
    const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    let ctx: AudioContext;
    try {
      ctx = new Ctor();
    } catch {
      return;
    }
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    const source = ctx.createMediaStreamSource(stream);
    source.connect(analyser);
    const samples = new Float32Array(analyser.fftSize);
    let frame = 0;
    let shown = 0;
    let last = performance.now();
    const tick = () => {
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) sum += sample * sample;
      // The fall is timed, not counted in frames: this runs on `requestAnimationFrame`, which a
      // browser throttles when the tab is not in front, and a per-frame fall then takes seconds.
      const now = performance.now();
      const elapsed = now - last;
      last = now;
      const next = decayLevel(shown, meterLevel(Math.sqrt(sum / samples.length)), elapsed / FALL_MS);
      // A whole percent of a 320px bar is three pixels.
      if (Math.abs(next - shown) >= 0.01) {
        shown = next;
        setLevel(next);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      source.disconnect();
      void ctx.close().catch(() => undefined);
    };
  }, [stream]);
  return level;
}

/**
 * Play the chime through `speakerId` (null: the system default) on `element`, or on a fresh one.
 *
 * Its own element, not the one the calls play through: this has to work with no call up, and
 * `RemoteAudio`'s element is routed to a call's stream. It does double duty as the page's first
 * deliberate playback, which is also what satisfies a browser's autoplay policy for later.
 * Rejects when the browser will not take the sink or refuses the playback — both end in the rep
 * hearing nothing, and the caller says what that means.
 */
export async function playTestTone(
  speakerId: string | null,
  element: HTMLAudioElement = new Audio(testToneWav()),
): Promise<HTMLAudioElement> {
  if (speakerId && typeof element.setSinkId === "function") await element.setSinkId(speakerId);
  element.currentTime = 0;
  await element.play();
  return element;
}

/** The Test button beside the speaker picker. */
function SpeakerTest({
  speakerId, disabled, onProblem,
}: {
  speakerId: string | null;
  disabled?: boolean;
  onProblem: (problem: string | null) => void;
}) {
  const [playing, setPlaying] = React.useState(false);
  const elementRef = React.useRef<HTMLAudioElement | null>(null);

  React.useEffect(
    () => () => {
      elementRef.current?.pause();
      elementRef.current = null;
    },
    [],
  );

  const play = async () => {
    const element = elementRef.current ?? new Audio(testToneWav());
    elementRef.current = element;
    element.onended = () => setPlaying(false);
    try {
      setPlaying(true);
      onProblem(null);
      await playTestTone(speakerId, element);
    } catch (e) {
      // Either the browser would not take the sink or it refused the playback. Both end in the
      // rep hearing nothing, which on its own reads as "this speaker is broken" — so say which
      // it was, since the call will fall back to the system default rather than go silent.
      setPlaying(false);
      onProblem(
        "That speaker could not be used, so calls will play through the system default instead.",
      );
      console.error("[audio-setup] test sound failed", e);
    }
  };

  return (
    <Button variant="ghost" size="sm" disabled={disabled} loading={playing} onClick={() => void play()}>
      <Volume2 />
      Test
    </Button>
  );
}
